import type { CapturedNetworkRequest, PostHog } from 'posthog-js'
import { env } from '@/infra/env'
import { Id } from '@/infra/id'
import type { Member } from '@/features/iam/members/Member'
import type { UserData, UserDataType } from '@/features/iam/users/UserData'
import type { TrackingService } from './TrackingService'

/**
 * Records sessions with PostHog.
 */
export class PostHogTrackingService implements TrackingService {
  private _postHog: PostHog | null = null

  /** @inheritDoc */
  identify(member: Member, userData: UserData) {
    void this._getPostHog().then((postHog) => {
      postHog.identify(Id.assert(member.user.id), {
        first_name: member.givenName,
        last_name: member.familyName,
        name: member.displayName,
        email: member.email,
        workspace_id: Id.assert(member.workspace.id),
        id: Id.assert(member.user.id),
        date_created: member.dateCreated.toISOString(),
        last_login: member.lastLogin?.toISOString() ?? null,
        user_type: formatUserTypeForTracking(userData.userDataType),
        workspace_name: member.workspace.name,
      })
    })
  }

  /** @inheritDoc */
  reset() {
    void this._getPostHog().then((postHog) => {
      postHog.stopSessionRecording()
      postHog.reset()
    })
  }

  /**
   * Initializes PostHog on first use, keeping it out of the main bundle.
   */
  private async _getPostHog(): Promise<PostHog> {
    if (!this._postHog) {
      const { posthog } = await import('posthog-js')
      this._postHog = posthog.init(env.POSTHOG_KEY, {
        api_host: 'https://ntsh.taxfyle.com',
        session_recording: {
          maskTextSelector: '[data-private]',
          maskInputOptions: { password: true, email: true },
          maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
            if (request.name?.includes('/messages')) {
              return {
                ...request,
                requestBody: '[Redacted]',
                responseBody: '[Redacted]',
              }
            }
            if (request.name?.includes('__authorization')) {
              return null
            }
            return request
          },
        },
      })
    }
    return this._postHog
  }

  static create() {
    return new PostHogTrackingService()
  }
}

/**
 * PostHog reports group users by the type codes the API uses.
 */
function formatUserTypeForTracking(type: UserDataType) {
  switch (type) {
    case 'User':
      return 'USER'
    case 'Pro':
      return 'PRO'
    case 'Admin':
      return 'ADMIN'
    case 'CallAgent':
      return 'CALL_AGENT'
  }
}
